import { useRef, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { PortfolioProvider } from "@/context/PortfolioContext";
import { MultiPortfolioProvider, setNavigationCallback, useMultiPortfolio } from "@/context/MultiPortfolioContext";
import { I18nProvider } from "@/context/I18nContext";
import { PageLoader } from "@/components/MoneyMachineLoader";

import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import AssetsPage from "@/pages/AssetsPage";
import CreditsPage from "@/pages/CreditsPage";
import OpportunitiesPage from "@/pages/OpportunitiesPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import ReportsPage from "@/pages/ReportsPage";
import BudgetPage from "@/pages/BudgetPage";
import AdminArchitecturePage from "@/pages/AdminArchitecturePage";
import DemoLogin from "@/pages/DemoLogin";

import "@/App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Component to set up navigation callback for portfolio switching
const NavigationSetup = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    setNavigationCallback((path) => {
      navigate(path, { replace: true });
    });
    return () => setNavigationCallback(null);
  }, [navigate]);
  
  return null;
};

const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);

      if (!sessionIdMatch) {
        navigate("/", { replace: true });
        return;
      }

      const sessionId = sessionIdMatch[1];

      try {
        const response = await axios.post(
          `${API_URL}/api/auth/session`,
          { session_id: sessionId },
          { withCredentials: true }
        );

        setUser(response.data);
        navigate("/dashboard", { replace: true, state: { user: response.data } });
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser]);

  return <PageLoader />;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchKey } = useMultiPortfolio();

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/", { replace: true });
    }
  }, [user, loading, navigate, location.state]);

  if (loading) return <PageLoader />;
  if (!user && !location.state?.user) return null;

  // Use switchKey to force remount PortfolioProvider when switching portfolios
  return (
    <PortfolioProvider key={`portfolio-${switchKey}`}>
      {children}
    </PortfolioProvider>
  );
};

const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL hash synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <NavigationSetup />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/demo" element={<DemoLogin />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute>
            <AssetsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/credits"
        element={
          <ProtectedRoute>
            <CreditsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/opportunities"
        element={
          <ProtectedRoute>
            <OpportunitiesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <ReportsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/budget"
        element={
          <ProtectedRoute>
            <BudgetPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/architecture"
        element={
          <ProtectedRoute>
            <AdminArchitecturePage />
          </ProtectedRoute>
        }
      />
    </Routes>
    </>
  );
};

function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <MultiPortfolioProvider>
            <BrowserRouter>
              <AppRouter />
              <Toaster 
                position="bottom-right" 
                richColors 
                theme="system"
              />
            </BrowserRouter>
          </MultiPortfolioProvider>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

export default App;
